import React, { useEffect, useState, useContext, useRef } from "react";
import clsx from "clsx";

import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import useSettings from "../../hooks/useSettings";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { canEditBrand } from "../../utils/tenant";
import { AuthContext } from "../../context/Auth/AuthContext";

import { IconButton, InputAdornment } from "@material-ui/core";

import { Colorize, AttachFile, Delete, ImageOutlined } from "@material-ui/icons";
import ColorPicker from "../ColorPicker";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

import defaultLogoLight from "../../assets/logo.png";
import defaultLogoDark from "../../assets/logo-black.png";
import defaultLogoFavicon from "../../assets/favicon.ico";
import ColorBoxModal from "../ColorBoxModal/index.js";
import Checkbox from "@material-ui/core/Checkbox";
import { i18n } from "../../translate/i18n";
import { Section } from "../ui";
import { usePanelStyles, FieldLabel } from "../FormFields/panel";
import { getTokens, LIGHT, DARK } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    container: { display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 },
    loading: { padding: "48px 0", textAlign: "center", fontSize: 13, color: t.textSecondary },
    head: { marginBottom: 16 },
    headTitle: { margin: 0, fontFamily: t.font, fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: t.text },
    headHint: { margin: "2px 0 0", fontFamily: t.font, fontSize: 13, lineHeight: 1.5, color: t.textSecondary },
    // amostra da cor escolhida (a cor é DADO da marca)
    colorAdorment: {
      width: 20,
      height: 20,
      borderRadius: t.radius.badge,
      border: `1px solid ${t.lineStrong}`,
    },
    colorField: { "& .MuiInputBase-root, & .MuiInputBase-input": { cursor: "pointer" } },
    iconButton: { width: 32, height: 32, padding: 0 },
    uploadInput: { display: "none" },
    uploadField: {
      "& .MuiInputBase-input": { cursor: "default", textOverflow: "ellipsis" },
      "& .MuiOutlinedInput-adornedEnd": { paddingRight: 6 },
      "& label": { display: "inline-flex" },
    },
    imagesGap: { marginTop: 18 },
    // Prévia da imagem. Os fundos claro/escuro são fixos de propósito: simulam a
    // superfície de cada tema, seja qual for o tema em uso agora.
    preview: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: 112,
      marginBottom: 8,
      padding: 14,
      overflow: "hidden",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      "& svg": { fontSize: 28 },
    },
    previewFlush: { padding: 0 },
    previewLight: { backgroundColor: LIGHT.paper, color: LIGHT.textMuted },
    previewDark: { backgroundColor: DARK.paper, color: DARK.textMuted },
    previewNeutral: { backgroundColor: t.muted, color: t.textMuted },
    previewImage: { maxWidth: "100%", maxHeight: 80, objectFit: "contain" },
    previewBackgroundImage: { width: "100%", height: "100%", objectFit: "cover" },
    languageGrid: { display: "flex", flexWrap: "wrap", gap: 10 },
    languageItem: {
      display: "inline-flex",
      alignItems: "center",
      minWidth: 132,
      padding: "2px 14px 2px 4px",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      backgroundColor: t.muted,
      cursor: "pointer",
      [theme.breakpoints.down("xs")]: { flex: "1 1 132px" },
    },
    languageLabel: { fontFamily: t.font, fontSize: 13.5, fontWeight: 500, color: t.text },
  };
});

const LANGUAGE_OPTIONS = [
  { code: "pt-BR", label: "Português" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "ar", label: "عربي" },
];

const SectionHead = ({ classes, title, description }) => (
  <div className={classes.head}>
    <h2 className={classes.headTitle}>{title}</h2>
    {description && <p className={classes.headHint}>{description}</p>}
  </div>
);

// Imagem da marca: prévia em cima, arquivo atual com enviar/excluir embaixo.
const BrandImage = ({
  classes,
  label,
  tone,
  flush,
  fieldId,
  inputId,
  inputRef,
  value,
  onUpload,
  onDelete,
  children,
}) => (
  <FieldLabel label={label} htmlFor={fieldId}>
    <div className={clsx(classes.preview, classes[tone], flush && classes.previewFlush)}>{children}</div>
    <TextField
      id={fieldId}
      variant="outlined"
      value={value || ""}
      size="small"
      fullWidth
      className={classes.uploadField}
      inputProps={{ "aria-label": label }}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <>
            {value && (
              <Tooltip title={i18n.t("whitelabel.delete")}>
                <IconButton
                  size="small"
                  color="default"
                  className={classes.iconButton}
                  aria-label={`${i18n.t("whitelabel.delete")} — ${label}`}
                  onClick={onDelete}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <input
              type="file"
              id={inputId}
              ref={inputRef}
              className={classes.uploadInput}
              onChange={onUpload}
            />
            <label htmlFor={inputId}>
              <Tooltip title={i18n.t("whitelabel.upload")}>
                <IconButton
                  size="small"
                  color="default"
                  className={classes.iconButton}
                  aria-label={`${i18n.t("whitelabel.upload")} — ${label}`}
                  onClick={() => {
                    inputRef.current.click();
                  }}
                >
                  <AttachFile fontSize="small" />
                </IconButton>
              </Tooltip>
            </label>
          </>
        ),
      }}
    />
  </FieldLabel>
);

export default function Whitelabel(props) {
  const { settings } = props;
  const classes = useStyles();
  const p = usePanelStyles();
  const [settingsLoaded, setSettingsLoaded] = useState({});

  // Revenda: permissão vem do `user` reativo do AuthContext (sem snapshot local)
  const { user: currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  // Marca efetiva do domínio acessado (cadeia empresa → revendedor → plataforma),
  // usada como placeholder quando o setting da própria empresa está vazio.
  const [inheritedBrand, setInheritedBrand] = useState({});
  const { colorMode } = useContext(ColorModeContext);
  const [primaryColorLightModalOpen, setPrimaryColorLightModalOpen] =
    useState(false);
  const [primaryColorDarkModalOpen, setPrimaryColorDarkModalOpen] =
    useState(false);

  const logoLightInput = useRef(null);
  const logoDarkInput = useRef(null);
  const logoFaviconInput = useRef(null);
  const backgroundLightInput = useRef(null);
  const backgroundDarkInput = useRef(null);
  const appNameInput = useRef(null);
  const [appName, setAppName] = useState(settingsLoaded.appName || "");
  // Termos de uso: link e versão (chaves de marca, herdadas pela cadeia)
  const [termsUrl, setTermsUrl] = useState("");
  const [termsVersion, setTermsVersion] = useState("");
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);

  const { update, getPublicBrand } = useSettings();

  function updateSettingsLoaded(key, value) {
    if (
      key === "primaryColorLight" ||
      key === "primaryColorDark" ||
      key === "appName"
    ) {
      localStorage.setItem(key, value);
    }
    const newSettings = { ...settingsLoaded };
    newSettings[key] = value;
    setSettingsLoaded(newSettings);
  }

  useEffect(() => {
    let isMounted = true;
    getPublicBrand()
      .then((brand) => {
        if (isMounted) setInheritedBrand(brand || {});
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    if (Array.isArray(settings) && settings.length) {
      const primaryColorLight = settings.find(
        (s) => s.key === "primaryColorLight"
      )?.value;
      const primaryColorDark = settings.find(
        (s) => s.key === "primaryColorDark"
      )?.value;
      const appLogoLight = settings.find(
        (s) => s.key === "appLogoLight"
      )?.value;
      const appLogoDark = settings.find((s) => s.key === "appLogoDark")?.value;
      const appLogoFavicon = settings.find(
        (s) => s.key === "appLogoFavicon"
      )?.value;
      const appLogoBackgroundLight = settings.find(
        (s) => s.key === "appLogoBackgroundLight"
      )?.value;
      const appLogoBackgroundDark = settings.find(
        (s) => s.key === "appLogoBackgroundDark"
      )?.value;
      const appName = settings.find((s) => s.key === "appName")?.value;
      const termsUrlSetting = settings.find((s) => s.key === "termsUrl")?.value;
      const termsVersionSetting = settings.find((s) => s.key === "termsVersion")?.value;
      const enabledLanguagesSetting = settings.find(
        (s) => s.key === "enabledLanguages"
      )?.value;
      let langs = ["pt-BR", "en"];
      try {
        if (enabledLanguagesSetting) {
          langs = JSON.parse(enabledLanguagesSetting);
        }
      } catch { }

      if (isMounted) {
        setAppName(appName || "");
        setTermsUrl(termsUrlSetting || "");
        setTermsVersion(termsVersionSetting || "");
        setEnabledLanguages(langs);
        setSettingsLoaded({
          ...settingsLoaded,
          primaryColorLight,
          primaryColorDark,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appLogoBackgroundLight,
          appLogoBackgroundDark,
          appName,
          termsUrl: termsUrlSetting,
          termsVersion: termsVersionSetting,
          enabledLanguages: langs,
        });
        setLoading(false);
      }
    }
    else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  // Valor herdado (revendedor/plataforma) a exibir como placeholder quando o
  // setting da própria empresa ainda está vazio. Não se aplica quando a marca
  // resolvida já é a da própria empresa.
  const inheritedValue = (key) => {
    const own =
      key === "appName"
        ? appName
        : key === "termsUrl"
        ? termsUrl
        : key === "termsVersion"
        ? termsVersion
        : settingsLoaded[key];
    if (own) return "";
    if (!inheritedBrand || !inheritedBrand[key]) return "";
    if (
      currentUser &&
      currentUser.companyId &&
      Number(inheritedBrand.companyId) === Number(currentUser.companyId)
    ) {
      return "";
    }
    return String(inheritedBrand[key]);
  };
  const inheritedHint = (key) =>
    inheritedValue(key) ? i18n.t("whitelabel.inheritedHint") : undefined;

  async function handleSaveSetting(key, value) {
    await update({
      key,
      value,
    });
    updateSettingsLoaded(key, value);
    toast.success("Operação atualizada com sucesso.");
  }

  async function handleSaveEnabledLanguages(newLangs) {
    await handleSaveSetting("enabledLanguages", newLangs);
    setEnabledLanguages(newLangs);
  }

  const uploadLogo = async (e, mode) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    const formData = new FormData();

    formData.append("typeArch", "logo");
    formData.append("mode", mode);
    formData.append("file", file);

    await api
      .post("/settings-whitelabel/logo", formData, {
        onUploadProgress: (event) => {
          let progress = Math.round((event.loaded * 100) / event.total);
          console.log(`A imagem está ${progress}% carregada... `);
        },
      })
      .then((response) => {
        updateSettingsLoaded(`appLogo${mode}`, response.data);
        if (mode === "BackgroundLight" || mode === "BackgroundDark") {
        } else {
          colorMode[`setAppLogo${mode}`](
            getBackendUrl() + "/public/" + response.data
          );
        }
      })
      .catch((err) => {
        console.error(`Houve um problema ao realizar o upload da imagem.`);
        console.log(err);
      });
  };

  if (loading) {
    return <div className={classes.loading}>Carregando configurações...</div>;
  }

  // Revenda: super, admin de revendedor ou admin de empresa com marca própria liberada
  if (!canEditBrand(currentUser)) {
    return null;
  }

  return (
    <div className={classes.container}>
      {/* Seção de Configurações Gerais */}
      <Section>
        <SectionHead
          classes={classes}
          title={i18n.t("whitelabel.sections.general")}
          description={i18n.t("whitelabel.sections.generalDescription")}
        />
        <div className={clsx(p.grid2, p.labelsAbove)}>
          <TextField
            id="appname-field"
            label={i18n.t("whitelabel.appName")}
            variant="outlined"
            name="appName"
            value={appName}
            placeholder={inheritedValue("appName") || undefined}
            helperText={inheritedHint("appName")}
            inputRef={appNameInput}
            onChange={(e) => {
              setAppName(e.target.value);
            }}
            onBlur={async (_) => {
              await handleSaveSetting("appName", appName);
              colorMode.setAppName(appName || "Digite AI");
            }}
            size="small"
            fullWidth
          />
          {/* Termos de uso: link + versão (mudar a versão força novo aceite na cadeia) */}
          <TextField
            id="terms-url-field"
            label={i18n.t("whitelabel.termsUrl")}
            variant="outlined"
            name="termsUrl"
            type="url"
            value={termsUrl}
            placeholder={inheritedValue("termsUrl") || "https://"}
            helperText={inheritedHint("termsUrl") || i18n.t("whitelabel.termsUrlHint")}
            onChange={(e) => setTermsUrl(e.target.value)}
            onBlur={async (_) => {
              await handleSaveSetting("termsUrl", termsUrl.trim());
            }}
            size="small"
            fullWidth
          />
          <TextField
            id="terms-version-field"
            label={i18n.t("whitelabel.termsVersion")}
            variant="outlined"
            name="termsVersion"
            value={termsVersion}
            placeholder={inheritedValue("termsVersion") || "2026-08-30"}
            helperText={inheritedHint("termsVersion") || i18n.t("whitelabel.termsVersionHint")}
            onChange={(e) => setTermsVersion(e.target.value)}
            onBlur={async (_) => {
              await handleSaveSetting("termsVersion", termsVersion.trim());
            }}
            size="small"
            fullWidth
          />
        </div>
      </Section>

      {/* Seção de Cores */}
      <Section>
        <SectionHead
          classes={classes}
          title={i18n.t("whitelabel.sections.colors")}
          description={i18n.t("whitelabel.sections.colorsDescription")}
        />
        <div className={clsx(p.grid2, p.labelsAbove)}>
          <TextField
            id="primary-color-light-field"
            label={i18n.t("whitelabel.primaryColorLight")}
            variant="outlined"
            value={settingsLoaded.primaryColorLight || ""}
            placeholder={inheritedValue("primaryColorLight") || undefined}
            helperText={inheritedHint("primaryColorLight")}
            onClick={() => setPrimaryColorLightModalOpen(true)}
            size="small"
            fullWidth
            className={classes.colorField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <div
                    style={{
                      backgroundColor:
                        settingsLoaded.primaryColorLight ||
                        inheritedValue("primaryColorLight") ||
                        undefined,
                    }}
                    className={classes.colorAdorment}
                  ></div>
                </InputAdornment>
              ),
              endAdornment: (
                <IconButton
                  size="small"
                  color="default"
                  className={classes.iconButton}
                  aria-label={`Escolher cor — ${i18n.t("whitelabel.primaryColorLight")}`}
                  onClick={() => setPrimaryColorLightModalOpen(true)}
                >
                  <Colorize fontSize="small" />
                </IconButton>
              ),
            }}
          />
          <TextField
            id="primary-color-dark-field"
            label={i18n.t("whitelabel.primaryColorDark")}
            variant="outlined"
            value={settingsLoaded.primaryColorDark || ""}
            placeholder={inheritedValue("primaryColorDark") || undefined}
            helperText={inheritedHint("primaryColorDark")}
            onClick={() => setPrimaryColorDarkModalOpen(true)}
            size="small"
            fullWidth
            className={classes.colorField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <div
                    style={{
                      backgroundColor:
                        settingsLoaded.primaryColorDark ||
                        inheritedValue("primaryColorDark") ||
                        undefined,
                    }}
                    className={classes.colorAdorment}
                  ></div>
                </InputAdornment>
              ),
              endAdornment: (
                <IconButton
                  size="small"
                  color="default"
                  className={classes.iconButton}
                  aria-label={`Escolher cor — ${i18n.t("whitelabel.primaryColorDark")}`}
                  onClick={() => setPrimaryColorDarkModalOpen(true)}
                >
                  <Colorize fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </div>
        <ColorBoxModal
          open={primaryColorLightModalOpen}
          handleClose={() => setPrimaryColorLightModalOpen(false)}
          onChange={(color) => {
            console.log("🔍 [Whitelabel] Color changed:", color);
            handleSaveSetting("primaryColorLight", `#${color.hex}`);
            colorMode.setPrimaryColorLight(`#${color.hex}`);
          }}
          currentColor={
            settingsLoaded.primaryColorLight ||
            inheritedValue("primaryColorLight") ||
            undefined
          }
        />
        <ColorBoxModal
          open={primaryColorDarkModalOpen}
          handleClose={() => setPrimaryColorDarkModalOpen(false)}
          onChange={(color) => {
            handleSaveSetting("primaryColorDark", `#${color.hex}`);
            colorMode.setPrimaryColorDark(`#${color.hex}`);
          }}
          currentColor={
            settingsLoaded.primaryColorDark ||
            inheritedValue("primaryColorDark") ||
            undefined
          }
        />
      </Section>

      {/* Seção de Logos e Imagens: cada imagem com a prévia e o arquivo no mesmo bloco */}
      <Section>
        <SectionHead
          classes={classes}
          title={i18n.t("whitelabel.sections.logos")}
          description={i18n.t("whitelabel.sections.logosDescription")}
        />
        <div className={p.grid3}>
          <BrandImage
            classes={classes}
            label={i18n.t("whitelabel.logoLight")}
            tone="previewLight"
            fieldId="logo-light-upload-field"
            inputId="upload-logo-light-button"
            inputRef={logoLightInput}
            value={settingsLoaded.appLogoLight}
            onUpload={(e) => uploadLogo(e, "Light")}
            onDelete={() => {
              handleSaveSetting("appLogoLight", "");
              colorMode.setAppLogoLight(defaultLogoLight);
            }}
          >
            <img
              className={classes.previewImage}
              src={settingsLoaded.appLogoLight ?
                getBackendUrl() + "/public/" + settingsLoaded.appLogoLight :
                defaultLogoLight
              }
              alt={i18n.t("whitelabel.preview") + " light-logo"}
              onError={(e) => {
                e.target.src = defaultLogoLight;
              }}
            />
          </BrandImage>

          <BrandImage
            classes={classes}
            label={i18n.t("whitelabel.logoDark")}
            tone="previewDark"
            fieldId="logo-dark-upload-field"
            inputId="upload-logo-dark-button"
            inputRef={logoDarkInput}
            value={settingsLoaded.appLogoDark}
            onUpload={(e) => uploadLogo(e, "Dark")}
            onDelete={() => {
              handleSaveSetting("appLogoDark", "");
              colorMode.setAppLogoDark(defaultLogoDark);
            }}
          >
            <img
              className={classes.previewImage}
              src={settingsLoaded.appLogoDark ?
                getBackendUrl() + "/public/" + settingsLoaded.appLogoDark :
                defaultLogoDark
              }
              alt={i18n.t("whitelabel.preview") + " dark-logo"}
              onError={(e) => {
                e.target.src = defaultLogoDark;
              }}
            />
          </BrandImage>

          <BrandImage
            classes={classes}
            label={i18n.t("whitelabel.favicon")}
            tone="previewNeutral"
            fieldId="logo-favicon-upload-field"
            inputId="upload-logo-favicon-button"
            inputRef={logoFaviconInput}
            value={settingsLoaded.appLogoFavicon}
            onUpload={(e) => uploadLogo(e, "Favicon")}
            onDelete={() => {
              handleSaveSetting("appLogoFavicon", "");
              colorMode.setAppLogoFavicon(defaultLogoFavicon);
            }}
          >
            <img
              className={classes.previewImage}
              src={settingsLoaded.appLogoFavicon ?
                getBackendUrl() + "/public/" + settingsLoaded.appLogoFavicon :
                defaultLogoFavicon
              }
              alt={i18n.t("whitelabel.preview") + " favicon"}
              onError={(e) => {
                e.target.src = defaultLogoFavicon;
              }}
            />
          </BrandImage>
        </div>

        <div className={clsx(p.grid2, classes.imagesGap)}>
          <BrandImage
            classes={classes}
            label={i18n.t("whitelabel.backgroundLight")}
            tone="previewLight"
            flush={Boolean(settingsLoaded.appLogoBackgroundLight)}
            fieldId="background-light-upload-field"
            inputId="upload-background-light-button"
            inputRef={backgroundLightInput}
            value={settingsLoaded.appLogoBackgroundLight}
            onUpload={(e) => uploadLogo(e, "BackgroundLight")}
            onDelete={() => {
              handleSaveSetting("appLogoBackgroundLight", "");
            }}
          >
            {settingsLoaded.appLogoBackgroundLight ? (
              <img
                className={classes.previewBackgroundImage}
                src={
                  getBackendUrl() +
                  "/public/" +
                  settingsLoaded.appLogoBackgroundLight
                }
                alt={i18n.t("whitelabel.preview") + " background-light"}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <ImageOutlined aria-hidden="true" />
            )}
          </BrandImage>

          <BrandImage
            classes={classes}
            label={i18n.t("whitelabel.backgroundDark")}
            tone="previewDark"
            flush={Boolean(settingsLoaded.appLogoBackgroundDark)}
            fieldId="background-dark-upload-field"
            inputId="upload-background-dark-button"
            inputRef={backgroundDarkInput}
            value={settingsLoaded.appLogoBackgroundDark}
            onUpload={(e) => uploadLogo(e, "BackgroundDark")}
            onDelete={() => {
              handleSaveSetting("appLogoBackgroundDark", "");
            }}
          >
            {settingsLoaded.appLogoBackgroundDark ? (
              <img
                className={classes.previewBackgroundImage}
                src={
                  getBackendUrl() +
                  "/public/" +
                  settingsLoaded.appLogoBackgroundDark
                }
                alt={i18n.t("whitelabel.preview") + " background-dark"}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <ImageOutlined aria-hidden="true" />
            )}
          </BrandImage>
        </div>
      </Section>

      {/* Seção de Idiomas */}
      <Section>
        <SectionHead
          classes={classes}
          title={i18n.t("whitelabel.sections.languages")}
          description={i18n.t("whitelabel.sections.languagesDescription")}
        />
        <div className={classes.languageGrid}>
          {LANGUAGE_OPTIONS.map((lang) => (
            <label key={lang.code} className={classes.languageItem}>
              <Checkbox
                checked={enabledLanguages.includes(lang.code)}
                onChange={(e) => {
                  let newLangs = e.target.checked
                    ? [...enabledLanguages, lang.code]
                    : enabledLanguages.filter((c) => c !== lang.code);
                  if (newLangs.length === 0) {
                    toast.error(
                      i18n.t("whitelabel.atLeastOneLanguage")
                    );
                    return;
                  }
                  handleSaveEnabledLanguages(newLangs);
                }}
                color="primary"
                size="small"
              />
              <span className={classes.languageLabel}>
                {lang.label}
              </span>
            </label>
          ))}
        </div>
      </Section>
    </div>
  );
}
